import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { FOLDER_BOX } from '../itemIds';
import { useEditorStore } from '~/shell/Store';
import DropFolder from '~/training/sequence/templates/items/quiz-match/components/dropFolder.png';
import DotsIcon from '~/assets/button/dotsVertical.svg';
import FolderPopupMenu from './FolderPopupMenu';
import FolderEditModal from './FolderEditModal';

const BoxBorder = styled.div`
  border-radius: 22px;
  width: 220px;
  height: 175px;
  position: relative;
  margin-left: auto;
  margin-right: auto;
  background-color: transparent;
  cursor: pointer;

  &:focus {
    .BoxShape {
      outline: 2px solid blue;
      outline-offset: 2px;
    }
  }
`;

const DotsButton = styled.button`
  position: absolute;
  top: -10px;
  right: -20px;
  background: rgba(255, 255, 255);
  border: none;
  border-radius: 10px;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }
`;

const BoxShape = styled.div`
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  z-index: 2;
  color: #ffffff;
  background-image: url(${DropFolder});
  background-color: #9a9a9a;
  background-position: center;
  background-size: cover;
  padding: 16px;
  border-radius: 22px;
  height: 175px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  line-height: 18px;
  text-align: center;
`;

const TextInput = styled.input`
  background: transparent;
  border: 1px solid transparent; /* stable sizing to avoid layout shift */
  padding: 2px; /* reserve space so hover/focus doesn't move text */
  color: #ffffff;
  font-size: 15px;
  line-height: 18px;
  text-align: center;
  width: 100%;
  outline: none;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  transition: border-color 0.12s ease;

  /* Remove previous purple hover outline to prevent movement */
  &:hover {
    border-color: transparent;
  }
`;

const TextArea = styled.textarea`
  background: transparent;
  border: 1px solid transparent; /* stable sizing */
  padding: 2px; /* reserve space */
  color: #ffffff;
  font-size: 15px;
  line-height: 18px;
  text-align: center;
  width: 100%;
  outline: none;
  resize: none;
  overflow: hidden;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  font-family: inherit;
  transition: border-color 0.12s ease;

  &:hover {
    border-color: transparent;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
`;

interface Props {
  value: string;
  editable?: boolean;
}

export type FolderBoxDefinition = ItemDefinition<typeof FOLDER_BOX, Props>;

export class FolderBox extends Item<typeof FOLDER_BOX, Props> {
  protected render({ value, editable, id }: RenderProps<Props>) {
    const { contentEditable, updateSequence, sequence } = useEditorStore();
    const [text, setText] = useState<string>(value || '');
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(
      null
    );
    const boxShapeRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const isEditable = contentEditable && editable;
    const shouldUseTextarea = isEditable; // Always use textarea for editable folders to allow proper text wrapping
    const MAX_CHARS = 80; // Maximum characters allowed for folder name

    useEffect(() => {
      setText(value || '');
    }, [value]);

    // Also listen to sequence changes directly
    useEffect(() => {
      if (!sequence || !sequence.items) return;

      const [elementId] = id.split('_');
      const question = sequence.items.find(
        (item: { __typename?: string }) => item.__typename === 'Question'
      );

      if (question && question.items) {
        const folder = question.items.find(
          (item: { id: number | string }) => item.id.toString() === elementId
        );

        if (folder && folder.feedback !== text) {
          setText(folder.feedback || '');
        }
      }
    }, [sequence]);

    // Auto-resize textarea when text changes
    useEffect(() => {
      if (shouldUseTextarea && textareaRef.current) {
        try {
          const textarea = textareaRef.current;
          textarea.style.height = 'auto';
          textarea.style.height = `${Math.min(textarea.scrollHeight, 100)}px`;
        } catch (error) {
          // Silently ignore DOM manipulation errors
        }
      }
    }, [text, shouldUseTextarea]);

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const cleanText = event.target.value || '';

      const [elementId, fieldType] = id.split('_');

      if (cleanText.trim() == '') {
        // Keep empty if user hasn't entered anything
        setText('');
        updateSequence(elementId, fieldType, '');
      } else {
        setText(cleanText);
        updateSequence(elementId, fieldType, cleanText);
      }
    };

    const handleInput = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (isEditable) {
        const newText = event.target.value;

        // Allow edits if:
        // 1. New text is within limit, OR
        // 2. New text is shorter than current text (allowing deletions)
        if (newText.length <= MAX_CHARS || newText.length < text.length) {
          setText(newText);

          // Auto-resize textarea if it's a textarea
          if (event.target.tagName === 'TEXTAREA' && textareaRef.current) {
            try {
              const textarea = textareaRef.current;
              textarea.style.height = 'auto';
              textarea.style.height = `${Math.min(
                textarea.scrollHeight,
                100
              )}px`;
            } catch (error) {
              // Silently ignore DOM manipulation errors
            }
          }
        }
      }
    };

    const handleBlurTextarea = (
      event: React.FocusEvent<HTMLTextAreaElement>
    ) => {
      const cleanText = event.target.value || '';

      const [elementId, fieldType] = id.split('_');

      if (cleanText.trim() == '') {
        setText('');
        updateSequence(elementId, fieldType, '');
      } else {
        setText(cleanText);
        updateSequence(elementId, fieldType, cleanText);
      }
    };

    const handleDotsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAnchorElement(event.currentTarget);
      setIsPopupOpen(true);
    };

    const handleEdit = () => {
      setIsEditModalOpen(true);
    };

    const getExistingQuestion = () => {
      if (!sequence || !sequence.items) return '';

      const [elementId] = id.split('_');
      const question = sequence.items.find(
        (item: { __typename?: string }) => item.__typename === 'Question'
      );

      if (question && question.items) {
        const folder = question.items.find(
          (item: { id: number | string }) => item.id.toString() === elementId
        );

        return folder?.value || '';
      }

      return '';
    };

    const handleSaveModal = (cardText: string, folderName: string) => {
      // Save the card text as a question for this folder
      // The cardText will be the question that appears in the game
      // Save it in the 'value' field which is where questions are stored
      const [elementId] = id.split('_');

      // Save card text first
      updateSequence(elementId, 'title', cardText, 'QUESTION_FIELD');

      // Small delay to ensure the first update completes before the second
      setTimeout(() => {
        updateSequence(elementId, 'feedback', folderName);
      }, 100);

      // Update local text state to reflect the new folder name
      setText(folderName);
    };

    return (
      <BoxBorder>
        <BoxShape ref={boxShapeRef} className="BoxShape">
          {shouldUseTextarea ? (
            <TextArea
              ref={textareaRef}
              value={text}
              onChange={handleInput}
              onBlur={isEditable ? handleBlurTextarea : undefined}
              readOnly={!isEditable}
              placeholder="Type folder here"
              data-folder-id={id.split('_')[0]}
              maxLength={MAX_CHARS}
              rows={1}
            />
          ) : (
            <TextInput
              value={text}
              onChange={handleInput}
              onBlur={isEditable ? handleBlur : undefined}
              readOnly={!isEditable}
              placeholder="Type folder here"
              data-folder-id={id.split('_')[0]}
              maxLength={MAX_CHARS}
            />
          )}
        </BoxShape>
        {contentEditable && (
          <DotsButton onClick={handleDotsClick}>
            <DotsIcon />
          </DotsButton>
        )}
        <FolderPopupMenu
          isOpen={isPopupOpen}
          anchorElement={anchorElement}
          onClose={() => setIsPopupOpen(false)}
          folderId={id}
          onEdit={handleEdit}
        />
        <FolderEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveModal}
          folderName={text}
          existingQuestion={getExistingQuestion()}
        />
      </BoxBorder>
    );
  }
}

registry[FOLDER_BOX] = FolderBox;
